/**
 * Krok „osobní údaje“ (`customerDetails`) — port z `old-js/modules/orderProcess/customerDetails.js`.
 * Digitální košík: skrytí telefonu / doručení na jinou adresu, vlastní „nakupuji na firmu“, práce s fakturačním boxem.
 * Fyzické: vlastní required hvězdičky + validace přes shoptet.validator.
 */

import {
  getCartGoodsType,
  normalizePageTypeName,
  safeGetShoptetDataLayer,
} from './checkout-helpers.js';

/** @param {Record<string, unknown>} cfg */
function log(cfg, ...args) {
  if (cfg?.debug !== true) return;
  // eslint-disable-next-line no-console
  console.log('[YG checkout]', ...args);
}

function pageIsCustomerDetails() {
  const pt = safeGetShoptetDataLayer()?.pageType;
  if (typeof pt !== 'string') return false;
  return normalizePageTypeName(pt) === 'customerdetails' || pt === 'customerDetails';
}

/** Shoptet ukazuje vybranou dopravu v souhrnu — musí přesně sedět s textem v adminu (viz config). */
function isPickupDeliverySelected(cfg) {
  const texts =
    cfg.checkout?.customerDetails?.pickupDeliveryRecapTexts ??
    cfg.checkout?.pickupDeliveryRecapTexts ??
    [];
  if (!Array.isArray(texts) || !texts.length) return false;
  const el = document.querySelector("[data-testid='recapDeliveryMethod']");
  const html = el?.innerHTML?.trim();
  if (!html) return false;
  return texts.some(t => String(t) === html);
}

function getShoptetValidator() {
  return window.shoptet?.validator;
}

function customCompanyCheckboxEl() {
  return document.getElementById('custom-checkbox-company-shopping');
}

function billingAddressWrap() {
  return document.querySelector('#checkoutContent .co-box.co-billing-address');
}

/** @param {ParentNode} root */
function getDigRequiredBillingInputEls(root) {
  return root.querySelectorAll(
    '#checkoutContent .co-billing-address input[type="text"]:not([name="vatId"])',
  );
}

/** @param {ParentNode} root */
function getDigAllBillingInputEls(root) {
  return root.querySelectorAll('#checkoutContent .co-billing-address input[type="text"]');
}

/** Přepíše label podle stavu required (jako starý kód). */
function changeRequiredStateBillingInputs(state) {
  const inputs = getDigRequiredBillingInputEls(document);
  inputs.forEach(element => {
    if (state) {
      element.classList.add('yg-custom-field-required-validation');
    } else {
      element.classList.remove('yg-custom-field-required-validation');
    }
    /** @type {HTMLElement} */
    const el = /** @type {HTMLElement} */ (element);
    const label = el.parentElement?.querySelector('label');
    if (!label) return;
    const labelText = label.textContent?.trim() ?? '';
    label.innerHTML = state
      ? `<span class="required-asterisk">${labelText}</span>`
      : labelText;
  });
}

function saveCompanyCredentialsToLS(toggle) {
  try {
    localStorage.setItem('customCheckboxCompanyShopping', toggle ? '1' : '0');
  } catch {
    /* ignore */
  }
}

/**
 * @param {Record<string, unknown>} cfg
 * @param {Event | null} event
 */
function toggleCompanyCredentials(cfg, event = null) {
  const checkbox = customCompanyCheckboxEl();
  const wrap = billingAddressWrap();
  if (!checkbox || !wrap) return;

  let toggle;
  if (event == null) {
    const valLs = localStorage.getItem('customCheckboxCompanyShopping');
    toggle = valLs === '1';
  } else {
    toggle = checkbox.checked;
  }

  checkbox.checked = toggle;
  wrap.style.display = toggle ? '' : 'none';
  if (!toggle && !isPickupDeliverySelected(cfg)) {
    customCompanyBillingRequiredValidationCheckAll(cfg);
  }

  changeRequiredStateBillingInputs(toggle);
  saveCompanyCredentialsToLS(toggle);
}

function customRequiredValidation(cfg, element) {
  const v = getShoptetValidator();
  if (!v || !(element instanceof HTMLInputElement)) return;
  const companyChecked = !!customCompanyCheckboxEl()?.checked;
  if (!companyChecked || (companyChecked && element.value.length)) {
    v.removeErrorMessage(element, 'validatorRequired');
  } else {
    v.addErrorMessage(element, 'validatorRequired');
  }
}

function customCompanyBillingRequiredValidationCheckAll(cfg) {
  getDigRequiredBillingInputEls(document).forEach(el => {
    if (el instanceof HTMLInputElement) customRequiredValidation(cfg, el);
  });
}

function clearCompanyBillingFields() {
  getDigAllBillingInputEls(document).forEach(el => {
    if (el instanceof HTMLInputElement) el.value = '';
  });
}

/**
 * @param {HTMLElement} parent
 * @param {Record<string, unknown>} cfg
 */
function getFyzRequiredInputEls(parent, cfg) {
  const another = /** @type {HTMLInputElement | null} */ (parent.querySelector('#another-shipping'));
  if (another?.checked) {
    return parent.querySelectorAll(
      '#phone, #billStreet, #billHouseNumber, #billCity, #billZip, #deliveryFullName, #deliveryStreet, #deliveryHouseNumber, #deliveryZip, #deliveryCity',
    );
  }
  if (isPickupDeliverySelected(cfg)) {
    return parent.querySelectorAll('#phone');
  }
  return parent.querySelectorAll('#phone, #billStreet, #billHouseNumber, #billCity, #billZip');
}

/**
 * @param {Record<string, unknown>} cfg
 * @param {HTMLElement} parent
 */
function setFyzRequiredInputs(cfg, parent) {
  const inputs = getFyzRequiredInputEls(parent, cfg);
  inputs.forEach(element => {
    if (!(element instanceof HTMLElement)) return;
    element.classList.add('yg-custom-field-required-validation');
    const id = element.getAttribute('id');
    const label =
      id === 'phone'
        ? element.parentElement?.parentElement?.querySelector('label')
        : element.parentElement?.querySelector('label');
    if (!label) return;
    const text = label.textContent?.trim() ?? '';
    label.innerHTML = `<span class="required-asterisk">${text}</span>`;
  });

  const another = parent.querySelector('#another-shipping');
  if (another && !another.dataset.ygFyzAnotherBound) {
    another.dataset.ygFyzAnotherBound = '1';
    another.addEventListener('click', () => {
      setFyzRequiredInputs(cfg, parent);
    });
  }
}

function customFyzRequiredValidation(element) {
  const v = getShoptetValidator();
  if (!v || !(element instanceof HTMLInputElement)) return;
  if (element.value.length) {
    v.removeErrorMessage(element, 'validatorRequired');
  } else {
    v.addErrorMessage(element, 'validatorRequired');
  }
}

function customFyzRequiredValidationCheckAll(cfg) {
  const parent = document.getElementById('checkoutContent');
  if (!parent) return;
  getFyzRequiredInputEls(parent, cfg).forEach(el => {
    if (el instanceof HTMLInputElement) customFyzRequiredValidation(el);
  });
}

function finalRequiredCheck() {
  const v = getShoptetValidator();
  const another = /** @type {HTMLInputElement | null} */ (document.querySelector('#another-shipping'));
  if (!another || another.checked || !v) return;
  const inputsToUnvalidate = document.querySelectorAll(
    '#deliveryFullName, #deliveryStreet, #deliveryHouseNumber, #deliveryZip, #deliveryCity',
  );
  inputsToUnvalidate.forEach(el => {
    if (el instanceof HTMLElement) {
      v.removeErrorMessage(el, 'validatorRequired');
      if (el instanceof HTMLInputElement) el.value = '';
      el.classList.remove('js-validate');
    }
  });
}

/**
 * @param {Record<string, unknown>} cfg
 * @param {HTMLElement} parent
 * @param {boolean} pickup
 * @param {1 | 2 | null} goodsType
 */
function setupDigitalOrPickup(cfg, parent, pickup, goodsType) {
  if (!pickup && goodsType === 1) {
    parent.querySelectorAll('.phone-form-group').forEach(el => {
      el.style.display = 'none';
    });
  } else {
    setFyzRequiredInputs(cfg, parent);
  }

  const another = parent.querySelector('#another-shipping');
  another?.parentElement && ((/** @type {HTMLElement} */ (another.parentElement)).style.display = 'none');

  const companyShop = /** @type {HTMLInputElement | null} */ (parent.querySelector('#company-shopping'));
  if (companyShop) {
    companyShop.checked = true;
    companyShop.parentElement && ((/** @type {HTMLElement} */ (companyShop.parentElement)).style.display =
      'none');
  }
  parent.querySelector('#company-info')?.classList.add('visible');

  if (!document.getElementById('custom-checkbox-company-shopping')) {
    const phoneGroup = parent.querySelector('.phone-form-group');
    const html = `<div class="form-group custom-checkbox-company-shopping">
    <input type="checkbox" name="custom-checkbox-company-shopping" id="custom-checkbox-company-shopping" value="1">
    <label for="custom-checkbox-company-shopping" class="whole-width">Nakupuji na firmu</label>
</div>`;
    if (phoneGroup) {
      phoneGroup.insertAdjacentHTML('afterend', html);
    } else {
      parent.insertAdjacentHTML('afterbegin', html);
    }
  }

  toggleCompanyCredentials(cfg, null);
  const cb = customCompanyCheckboxEl();
  if (cb && !cb.dataset.ygBound) {
    cb.dataset.ygBound = '1';
    cb.addEventListener('change', ev => toggleCompanyCredentials(cfg, ev));
  }

  getDigRequiredBillingInputEls(document).forEach(el => {
    if (el instanceof HTMLInputElement && !el.dataset.ygDigInputBound) {
      el.dataset.ygDigInputBound = '1';
      el.addEventListener('input', e => {
        const t = e.target;
        if (t instanceof HTMLInputElement) customRequiredValidation(cfg, t);
      });
    }
  });

  const submitBtn = document.getElementById('submit-order');
  if (submitBtn && !submitBtn.dataset.ygDigSubmitBound) {
    submitBtn.dataset.ygDigSubmitBound = '1';
    submitBtn.addEventListener('click', () => {
      customCompanyBillingRequiredValidationCheckAll(cfg);
      if (isPickupDeliverySelected(cfg)) {
        const phone = /** @type {HTMLInputElement | null} */ (document.querySelector('#phone'));
        const v = getShoptetValidator();
        if (phone && v && phone.value.length !== 9) {
          v.addErrorMessage(phone, 'validatorRequired');
        }
      }
      if (
        !customCompanyCheckboxEl()?.checked &&
        !document.querySelector('#checkoutContent .co-box.co-billing-address .js-error-field')
      ) {
        clearCompanyBillingFields();
      }
    });
  }
}

/**
 * @param {Record<string, unknown>} cfg
 * @param {HTMLElement} parent
 */
function setupPhysical(cfg, parent) {
  setFyzRequiredInputs(cfg, parent);
  const inputs = getFyzRequiredInputEls(parent, cfg);
  inputs.forEach(el => {
    if (el instanceof HTMLInputElement && !el.dataset.ygFyzInputBound) {
      el.dataset.ygFyzInputBound = '1';
      el.addEventListener('input', e => {
        const t = e.target;
        if (t instanceof HTMLInputElement) customFyzRequiredValidation(t);
      });
    }
  });

  const submitBtn = document.getElementById('submit-order');
  if (submitBtn && !submitBtn.dataset.ygFyzSubmitBound) {
    submitBtn.dataset.ygFyzSubmitBound = '1';
    submitBtn.addEventListener('click', () => {
      finalRequiredCheck();
      customFyzRequiredValidationCheckAll(cfg);
    });
  }
}

/** Jedna aplikace (idempotentní přes `data-yg-cd-init`). */
export function applyCustomerDetailsCheckout(cfg) {
  if (!pageIsCustomerDetails()) {
    try {
      localStorage.removeItem('customCheckboxCompanyShopping');
    } catch {
      /* ignore */
    }
    return;
  }

  const parent = document.getElementById('checkoutContent');
  if (!parent) return;

  const cd = cfg.checkout?.customerDetails;
  if (cd && typeof cd === 'object' && cd.enabled === false) return;

  if (parent.dataset.ygCdInit === '1') return;

  const dl = safeGetShoptetDataLayer();
  const goodsType = getCartGoodsType(cfg, dl);
  const pickup = isPickupDeliverySelected(cfg);

  if (goodsType === 1 || pickup) {
    setupDigitalOrPickup(cfg, parent, pickup, goodsType);
  } else {
    setupPhysical(cfg, parent);
  }

  parent.dataset.ygCdInit = '1';
  log(cfg, 'customerDetails applied', { goodsType, pickup });
}

let installScheduled = false;

/** @param {() => Record<string, unknown>} getCfg */
export function installCheckoutCustomerDetails(getCfg) {
  const run = () => {
    try {
      applyCustomerDetailsCheckout(getCfg());
    } catch (e) {
      const cfg = getCfg();
      if (cfg.debug === true) {
        // eslint-disable-next-line no-console
        console.error('[YG checkout] customerDetails error', e);
      }
    }
  };

  document.addEventListener('ShoptetDOMContentLoaded', run);
  if (!installScheduled) {
    installScheduled = true;
    document.addEventListener('ShoptetCartUpdated', run);
    setTimeout(run, 400);
    setTimeout(run, 1200);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
}
